import { Link } from '@tanstack/react-router'
import { formatPerComparableUnit, penalidade } from '@domain/product'
import type { FamilyComparison } from '@domain/product'

/**
 * A comparação entre os tamanhos de uma família, desenhada.
 *
 * ## Um componente só, usado em dois lugares
 *
 * O detalhe do produto e a tela do grupo mostram exatamente esta lista, e
 * duas cópias divergiriam no primeiro ajuste — uma passaria a mostrar a
 * penalidade e a outra não, e ninguém notaria por semanas. A diferença entre os
 * dois usos cabe num parâmetro: `destaqueId` é o produto que a pessoa já está
 * olhando, e que por isso não vira link para si mesmo.
 *
 * ## O que ela NÃO esconde
 *
 * Variação em outra base (ml contra g) e variação sem preço aparecem
 * **nomeadas**, fora da ordenação. Uma lista que as omitisse faria a pessoa
 * achar que viu a família inteira — e decidir por uma comparação parcial é pior
 * do que não comparar.
 */
export function ComparacaoDeFamilia({
  comparacao,
  nome,
  destaqueId,
}: {
  comparacao: FamilyComparison
  /** Para o rótulo da lista. */
  nome: string
  /** O produto que está sendo olhado agora, quando há um. */
  destaqueId?: string
}) {
  const pior = penalidade(comparacao)

  return (
    <>
      {comparacao.ranked.length > 0 ? (
        <ul aria-label={`Tamanhos de ${nome}`}>
          {comparacao.ranked.map((v, indice) => (
            <li
              key={v.product.id}
              className="flex flex-wrap items-baseline gap-2 border-b border-[var(--border)] py-1 text-sm"
            >
              <span aria-hidden="true">{indice === 0 ? '🏆' : '  '}</span>
              {v.product.id === destaqueId ? (
                <span className="min-w-0 flex-1 font-medium">{v.product.name}</span>
              ) : (
                <Link
                  to="/produtos/$id"
                  params={{ id: v.product.id }}
                  className="min-w-0 flex-1 underline"
                >
                  {v.product.name}
                </Link>
              )}
              {/* Qual dos dois números foi usado fica DITO, e não implícito:
                  "o que costumo pagar" e "o mais barato que já vi" são
                  respostas para perguntas diferentes. */}
              <span className="text-xs text-[var(--text-muted)]">
                {v.usouUltimo ? 'último pago' : 'menor visto'}
              </span>
              <span className="tabular-nums">
                {formatPerComparableUnit(v.referenceMilliCents, v.baseUnit)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-[var(--text-muted)]">
          Nenhum tamanho desta família tem preço registrado ainda.
        </p>
      )}

      {pior !== null && pior > 0.05 && (
        <p className="mt-2 text-xs text-[var(--text-muted)]">
          {`A mais cara sai ${String(Math.round(pior * 100))}% acima da mais barata.`}
        </p>
      )}

      {comparacao.incomparable.length > 0 && (
        <p className="mt-2 text-xs text-[var(--text-muted)]">
          {`Fora da comparação, por estarem em outra unidade: ${comparacao.incomparable.map((p) => p.name).join(', ')}.`}
        </p>
      )}

      {comparacao.semPreco.length > 0 && (
        <p className="mt-1 text-xs text-[var(--text-muted)]">
          {`Sem preço registrado: ${comparacao.semPreco.map((p) => p.name).join(', ')}.`}
        </p>
      )}
    </>
  )
}
